import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../store/auth.slice';
import { Button, IconButton, TextLink } from '../../../shared/ui';
import { timezoneList } from '../../../assets/timezone';
import { getTime, getTimeWithTZ, getMyTimeZone } from '../../../utils/getTime';
import { buildConsultationInviteText } from '../../../utils/buildConsultationInviteText';
import { IClient, IConsultation } from '../../../store/types';
import { EditIcon } from '../../icons/edit';
import { TrashIcon } from '../../icons/trash';
import { ConsultationShare } from '../consultation-share';
import { useGetSpecializationsQuery } from '../../../services/directus.api';
import { TherapyGroupIcon } from '../../icons/therapy-group';
import { TherapyIndividualIcon } from '../../icons/therapy-individual';

import { navigateToJournalSession } from '../../../pages/journal/journal-navigation';

type CardProps = {
  item: IConsultation;
  handleChangeCons: (
    id: number,
    options?: { isEdit?: boolean; overrideStart?: string; overrideEnd?: string; overrideDate?: string },
  ) => void;
  client?: Pick<IClient, 'id' | 'full_name' | 'time_zone'>;
  openEditFormClient: (id: number) => void;
  sortConsultation: () => void;
  handleOpenDelete: (item: IConsultation, isCalendar: boolean) => void;
};

export const ConsultationCard: FC<CardProps> = ({
  item,
  handleChangeCons,
  handleOpenDelete,
  openEditFormClient,
  client,
}) => {
  const navigate = useNavigate();
  const userData = useAppSelector(selectUser);
  const { data: specializationResponse } = useGetSpecializationsQuery();
  const [copy, setCopy] = useState(false);
  const [myTZ, setMyTZ] = useState<string>('');

  useEffect(() => {
    if (!copy) return;

    const timer = setTimeout(() => {
      setCopy(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [copy]);

  useEffect(() => {
    getMyTimeZone().then(setMyTZ);
  }, []);

  const tzOffset = timezoneList.find((el) => el.name === item.client_timezone)?.offset;
  const hasJournalResult = Array.isArray(item.result) && item.result.length > 0;

  const inviteText = useMemo(() => {
    const defaultSpecialization = 'Психолог';
    const specializationItems = specializationResponse?.data ?? [];
    const selectedSpecialization = specializationItems.find(
      (option) => String(option.id) === String(userData?.specialization ?? ''),
    );
    const specializationText = selectedSpecialization?.title || defaultSpecialization;
    const psychNameFromUserFallback = [userData?.last_name, userData?.first_name, userData?.middle_name]
      .filter(Boolean)
      .join(' ');

    return buildConsultationInviteText(item, {
      specializationText,
      psychNameFromUserFallback,
      clientFullName: client?.full_name,
    });
  }, [item, specializationResponse, userData, client?.full_name]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(inviteText).then(
      () => {
        setCopy(true);
        toast.success('Скопировано');
      },
      () => toast.error('Не удалось скопировать'),
    );
  };

  const handleOpenTranscription = () => {
    if (!client?.id) return;
    navigateToJournalSession(navigate, item.id, item.client_entity, 'Транскрипция');
  };

  return (
    <div className="w-full rounded-card flex flex-col w-full bg-white p-[20px] gap-[25px] relative">
      <div className="absolute right-5 top-5 text-ink-subtle" aria-hidden="true">
        {item.is_group ? (
          <TherapyGroupIcon width={20} height={20} className="text-ink-subtle" />
        ) : (
          <TherapyIndividualIcon width={20} height={20} className="text-ink-subtle" />
        )}
      </div>
      <div className="flex flex-col gap-[5px]">
        <p className="text-ink-subtle text-ui">{item.is_group ? 'Группа:' : 'Клиент:'}</p>
        <div className="flex gap-[10px] items-center">
          <p className="text-heading-md text-ink">{client?.full_name?.split('-')[0]}</p>
          {!item.is_group && (
            <IconButton
              label="Редактировать клиента"
              className="group -my-2"
              onClick={() => openEditFormClient(client?.id || 0)}
            >
              <EditIcon
                width={16}
                height={16}
                stroke="currentColor"
                className="transition-colors text-ink group-hover:text-primary"
              />
            </IconButton>
          )}
          <ConsultationShare inviteText={inviteText} link={item.link} />
        </div>
      </div>
      <div className="flex flex-col gap-[15px]">
        <div className="flex gap-[15px]">
          <p className="text-ink-subtle">{item.is_group ? 'Время встречи:' : 'Ваше время:'}</p>
          <p className="text-ink">{`${getTime(new Date(item.datetime_start))} - ${getTime(
            new Date(item.datetime_end),
          )} (${myTZ})`}</p>
        </div>
        {!item.is_group && (
          <div className="flex gap-[15px]">
            <p className="text-ink-subtle">Время у клиента:</p>
            <p className="text-ink">{`${getTimeWithTZ(
              new Date(item.datetime_start),
              Number(tzOffset),
            )} - ${getTimeWithTZ(new Date(item.datetime_end), Number(tzOffset))} (${item.client_timezone})`}</p>
          </div>
        )}
        <div className="flex flex-col md-plus:flex-row lg:flex-col xl:flex-row xl:items-center md-plus:gap-[15px]">
          <p className="text-ink-subtle">Ссылка для подключения:</p>
          <div className="flex items-center gap-[5px]">
            <TextLink to={item.link} target="_blank">
              {item.link.split('://')[1]}
            </TextLink>
            <IconButton label="Скопировать текст приглашения" className="-my-1.5" onClick={handleCopy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 11C6 8.172 6 6.757 6.879 5.879C7.757 5 9.172 5 12 5H15C17.828 5 19.243 5 20.121 5.879C21 6.757 21 8.172 21 11V16C21 18.828 21 20.243 20.121 21.121C19.243 22 17.828 22 15 22H12C9.172 22 7.757 22 6.879 21.121C6 20.243 6 18.828 6 16V11Z"
                  stroke="#C00000"
                  strokeWidth="1.5"
                  strokeOpacity={copy ? '0.4' : '1'}
                />
                <path
                  d="M6 19C5.20435 19 4.44129 18.6839 3.87868 18.1213C3.31607 17.5587 3 16.7956 3 16V10C3 6.229 3 4.343 4.172 3.172C5.343 2 7.229 2 11 2H15C15.7956 2 16.5587 2.31607 17.1213 2.87868C17.6839 3.44129 18 4.20435 18 5"
                  stroke="#C00000"
                  strokeWidth="1.5"
                  strokeOpacity={copy ? '0.4' : '1'}
                />
              </svg>
            </IconButton>
          </div>
        </div>
      </div>
      <div className="w-full flex flex-col md-plus:flex-row lg:flex-col xl:flex-row justify-between gap-4 items-center">
        <div className="w-full flex gap-[4px]">
          <Button
            size="md"
            variant="neutral"
            className="w-full xl:max-w-[152px]"
            onClick={() => handleChangeCons(item.id)}
          >
            Изменить
          </Button>
          {/* Neutral like "Изменить", but the red icon on hover says this one is destructive. */}
          <Button
            size="md"
            variant="neutral"
            iconOnly
            className="group"
            aria-label="Удалить встречу"
            onClick={() => handleOpenDelete(item, false)}
          >
            <TrashIcon
              width={22}
              height={22}
              stroke="currentColor"
              className="text-ink-muted group-hover:text-primary"
            />
          </Button>
        </div>
        {item.consultation_type === 'offline' && item.lifecycle_status === 'ready' && client?.id ? (
          <Button onClick={handleOpenTranscription} size="md" variant="dark" className="w-full xl:max-w-[286px]">
            Открыть транскрипцию
          </Button>
        ) : item.consultation_type !== 'offline' ? (
          <Button
            onClick={() => {
              window.open(`/meet/${item.code}`, '_blank');
            }}
            size="md"
            variant="dark"
            className="w-full xl:max-w-[286px]"
          >
            Подключиться к встрече
          </Button>
        ) : null}
      </div>
    </div>
  );
};
